import React, { Component, ErrorInfo, ReactNode } from "react";
import Alert from "./Alert";
import { Link } from "react-router-dom";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  level?: "global" | "page";
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

// Shared Tailwind button styles (the fallback UIs are Bootstrap-free).
const BTN =
  "tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-md tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-no-underline tw-transition";
const BTN_PRIMARY = `${BTN} tw-bg-sky-600 tw-text-white hover:tw-bg-sky-700`;
const BTN_SECONDARY = `${BTN} tw-bg-slate-200 tw-text-slate-700 hover:tw-bg-slate-300`;
const BTN_OUTLINE = `${BTN} tw-border tw-border-solid tw-border-sky-600 tw-text-sky-600 hover:tw-bg-sky-50`;
const BTN_SM = "tw-px-3 tw-py-1.5 tw-text-xs";

/**
 * Error Boundary Component
 *
 * Catches JavaScript errors anywhere in the child component tree, logs them,
 * and displays a Tailwind fallback UI instead of crashing the whole app.
 * Different UI for global vs page-level errors.
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("❌ ErrorBoundary caught an error:", error, errorInfo);
    this.setState({ error, errorInfo });
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { level = "page" } = this.props;
      const { error, errorInfo } = this.state;
      const isDev = process.env.NODE_ENV === "development";

      // Global error boundary — full-screen, prominent
      if (level === "global") {
        return (
          <div className="tw-min-h-screen tw-bg-slate-50 tw-px-4 tw-py-12">
            <div className="tw-mx-auto tw-max-w-xl">
              <div className="tw-rounded-lg tw-border tw-border-solid tw-border-slate-200 tw-bg-white tw-shadow-sm">
                <div className="tw-p-8 tw-text-center">
                  <div className="tw-mb-4">
                    <i
                      className="ri-error-warning-line"
                      style={{ fontSize: "4rem", color: "#dc3545" }}
                    ></i>
                  </div>
                  <h2 className="tw-mb-3 tw-text-2xl tw-font-semibold tw-text-slate-800">
                    Something went wrong
                  </h2>
                  <p className="tw-mb-5 tw-text-slate-500">
                    We encountered an unexpected error. Don&apos;t worry, your
                    data is safe.
                  </p>

                  {isDev && error && (
                    <Alert color="danger" className="tw-mb-5 tw-text-left">
                      <strong>Error Details:</strong>
                      <pre
                        className="tw-mb-0 tw-mt-2 tw-whitespace-pre-wrap"
                        style={{ fontSize: "0.875rem" }}
                      >
                        {error.toString()}
                        {errorInfo?.componentStack && (
                          <>
                            {"\n\nComponent Stack:"}
                            {errorInfo.componentStack}
                          </>
                        )}
                      </pre>
                    </Alert>
                  )}

                  <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-2">
                    <button onClick={this.handleReload} className={BTN_PRIMARY}>
                      <i className="ri-refresh-line"></i> Reload Page
                    </button>
                    <button onClick={this.handleReset} className={BTN_SECONDARY}>
                      <i className="ri-restart-line"></i> Try Again
                    </button>
                    <Link to="/dashboard" className={BTN_OUTLINE}>
                      <i className="ri-home-line"></i> Go Home
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      }

      // Page-level error boundary — inline alert
      return (
        <div className="tw-p-4">
          <Alert color="danger" className="tw-mb-3">
            <div className="tw-flex tw-items-start tw-gap-3">
              <i
                className="ri-error-warning-line"
                style={{ fontSize: "1.5rem" }}
              ></i>
              <div className="tw-flex-1 tw-min-w-0">
                <h5 className="tw-mb-2 tw-text-base tw-font-semibold">
                  An error occurred
                </h5>
                <p className="tw-mb-2">
                  This page encountered an error. You can try reloading or go
                  back to the previous page.
                </p>

                {isDev && error && (
                  <details className="tw-mt-3">
                    <summary style={{ cursor: "pointer" }}>
                      <small>Error Details (Development Only)</small>
                    </summary>
                    <pre
                      className="tw-mb-0 tw-mt-2 tw-whitespace-pre-wrap"
                      style={{ fontSize: "0.75rem" }}
                    >
                      {error.toString()}
                      {errorInfo?.componentStack && (
                        <>
                          {"\n\nComponent Stack:"}
                          {errorInfo.componentStack}
                        </>
                      )}
                    </pre>
                  </details>
                )}

                <div className="tw-mt-3 tw-flex tw-flex-wrap tw-gap-2">
                  <button
                    onClick={this.handleReload}
                    className={`${BTN_PRIMARY} ${BTN_SM}`}
                  >
                    <i className="ri-refresh-line"></i> Reload
                  </button>
                  <button
                    onClick={() => window.history.back()}
                    className={`${BTN_SECONDARY} ${BTN_SM}`}
                  >
                    <i className="ri-arrow-left-line"></i> Go Back
                  </button>
                  <Link to="/dashboard" className={`${BTN_OUTLINE} ${BTN_SM}`}>
                    <i className="ri-home-line"></i> Home
                  </Link>
                </div>
              </div>
            </div>
          </Alert>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
